<template>
    <Modal name="payment" modal-class="payment" @closed="ok">
        <template #head>
            <span v-if="!loading && !successType" class="payment__modal-title">
                {{ $t('payment.title') }}
            </span>
        </template>
        <template #content>
            <Spinner v-if="!ready || loading" class="spinner--opaque">
                {{ loadingText }}
            </Spinner>
            <div class="payment__inner">
                <template v-if="!successType && !$route.query.payment">
                    <div class="payment__cover-wrap">
                        <div class="payment__cover">
                            <img
                                v-if="book.preview_img_site"
                                :src="book.preview_img_site"
                                class="payment__cover-image"
                                :alt="book.name"
                            />
                        </div>
                    </div>
                    <div class="payment__form-wrap">
                        <form class="payment__form" @submit.prevent="getLink">
                            <div class="payment__book">
                                <h3 class="payment__title">{{ book.name }}</h3>
                                <span class="payment__label">{{ authorName }}</span>
                            </div>
                            <div class="payment__info">
                                <div v-if="book.symbols" class="payment__info-row">
                                    <div class="payment__info-label">
                                        <span class="payment__label">
                                            {{ $t('booksList.size') }}
                                        </span>
                                    </div>
                                    <div class="payment__info-col">
                                        <div class="payment__text">{{ bookSize }}</div>
                                        <div class="payment__text">
                                            {{ bookDuration }}
                                        </div>
                                    </div>
                                </div>
                                <div class="payment__info-row payment__info-row--block">
                                    <div class="payment__info-label">
                                        <span class="payment__label">
                                            {{ $t('payment.packages') }}
                                        </span>
                                    </div>
                                    <div class="payment__tariffs-wrap">
                                        <LimitsList
                                            v-model.number="limit"
                                            selectable
                                            :limits="filteredLimits"
                                        />
                                    </div>
                                </div>
                                <div class="payment__info-row">
                                    <div class="payment__info-label">
                                        <span class="payment__label">{{ $t('misc.voice') }}</span>
                                    </div>
                                    <div class="payment__info-col">
                                        <SpeakerSelect v-model="speaker" :speakers="speakers">
                                            <template #help>
                                                <div class="payment__speaker-help">
                                                    {{ $t('payment.help.common') }}
                                                </div>
                                                <div
                                                    v-if="!speakerConfigurableStress"
                                                    class="payment__speaker-help"
                                                >
                                                    {{ $t('payment.help.noStress') }}
                                                </div>
                                            </template>
                                        </SpeakerSelect>
                                    </div>
                                </div>
                                <div class="payment__info-row payment__info-row--promo">
                                    <div class="payment__info-label">
                                        <span class="payment__label">
                                            {{ $t('payment.promocode') }}
                                        </span>
                                    </div>
                                    <PromocodeInput v-model="promo" :error="error" />
                                </div>
                                <div class="payment__info-row">
                                    <div class="payment__info-label">
                                        <span class="payment__label">
                                            {{ $t('payment.total') }}
                                        </span>
                                    </div>
                                    <div class="payment__info-col">
                                        <div class="payment__price">
                                            <div
                                                v-tooltip="{
                                                    popperClass: 'payment__price-tooltip',
                                                    content: $t('payment.help.priceSymbolsNotice'),
                                                }"
                                                class="payment__price-final"
                                            >
                                                {{ formatCurrency(final) }}
                                            </div>
                                            <transition name="fade">
                                                <span
                                                    v-if="price && price !== final"
                                                    class="payment__price-initial"
                                                >
                                                    {{ formatCurrency(price) }}
                                                </span>
                                            </transition>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="payment__submit">
                                <button
                                    :disabled="paymentDisabled"
                                    :class="{
                                        'is-disabled': paymentDisabled,
                                    }"
                                    class="button button--big payment__button"
                                >
                                    {{ $t('payment.pay') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </template>
                <PaymentSuccess v-else :success="successType" @ok="ok" />
            </div>
        </template>
    </Modal>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import LimitsList from '../LimitsList/LimitsList';
import PromocodeInput from '../PromocodeInput/PromocodeInput';
import PaymentSuccess from './PaymentSuccess';

import { mapGetters, mapState, mapActions } from 'vuex';
import { parseQuery, formatDuration } from '@speechki-frontend/shared/helpers/functions';

import { fetchPrice, fetchLink, checkPayment } from '../../api/payment';
import { fetchBook } from '@speechki-frontend/core/book/api/books';

export default {
    name: 'Payment',
    components: {
        Modal,
        SpeakerSelect,
        LimitsList,
        PaymentSuccess,
        PromocodeInput,
    },
    data() {
        return {
            price: 0,
            final: 0,
            promo: '',
            error: null,
            loading: false,
            loadingText: '',
            successType: false,
            limit: null,
            speaker: null,
        };
    },
    computed: {
        ...mapState({
            book: (state) => state.book.book,
            limits: (state) => state.payment.limits,
            contentTypes: (state) => state.payment.contentTypes,
        }),
        ...mapGetters([
            'isAdmin',
            'isStaff',
            'userCurrency',
            'bookContentTypeId',
            'authorName',
            'bookSize',
        ]),

        ready() {
            return !!(this.bookId && this.limits.length && this.contentTypes.length);
        },

        bookId() {
            return this.book.id || Number(this.$route.params.id);
        },

        filteredLimits() {
            return this.limits.filter((lim) => lim.name !== 'free');
        },

        speakers() {
            if (!this.limit) return [];

            const { speakers = [] } = this.limits.find((lim) => lim.id === this.limit) || {};

            return speakers.filter((speaker) => speaker.book_language === this.book.book_language);
        },

        speakerObject() {
            return this.speakers.find((speaker) => speaker.id === this.speaker) || {};
        },

        bookDuration() {
            const { duration_ratio } = this.speakerObject;

            if (!duration_ratio) return '';

            return formatDuration(
                (duration_ratio * this.book.symbols) / 0.96 / this.book.speed,
                this.$i18n.locale
            );
        },

        speakerConfigurableStress() {
            if (!this.speaker) return {};

            const { extra_params: settings = {} } = this.speakerObject;

            return settings.accent && settings.accent_type === 'symbol';
        },

        paymentDisabled() {
            return this.error || this.loading || !this.speaker || !this.limit;
        },
    },
    watch: {
        '$route.query': {
            immediate: true,
            handler() {
                if (this.$route.query.payment) {
                    if (this.$route.query.receipt_id) {
                        this.checkPayment(this.$route.query.receipt_id);
                    } else {
                        this.checkBookLimit(this.$route.params.id);
                    }
                }
            },
        },

        async limit(_, old) {
            if (old) this.speaker = null;
        },

        speaker() {
            this.fetchPrice();
        },
        promo() {
            this.fetchPrice();
        },
    },

    async created() {
        await Promise.all([
            this.fetchLimits({ currency: this.userCurrency }),
            this.fetchContentTypes(),
        ]);

        const { id } =
            this.filteredLimits.find((lim) => {
                return !!lim.speakers.find((sp) => sp.id === this.book.speaker.id);
            }) || {};

        if (id) {
            this.limit = id;
            this.speaker = this.book.speaker.id;
        }
    },

    methods: {
        ...mapActions(['fetchLimits', 'fetchContentTypes']),

        async checkBookLimit(id) {
            try {
                this.loading = true;

                const book = await fetchBook(id);

                if (book.limit && book.limit_info && !book.limit_info.is_trial) {
                    this.successType = 'has-limit';
                } else {
                    this.successType = 'no-limit';
                }

                this.loading = false;
            } catch (error) {
                this.loading = false;
                console.log(error);
            }
        },

        async fetchPrice() {
            if (!this.limit || !this.bookContentTypeId || !this.bookId) {
                return;
            }

            try {
                this.error = null;
                const data = {
                    limit: this.limit,
                    content_type: this.bookContentTypeId,
                    object_id: this.bookId,
                    type_payment: 'buy',
                    promo_code: this.promo,
                    speaker: this.speaker,
                };

                const response = await fetchPrice(data);

                // stripe uses cents, so
                if (this.userCurrency === 'USD') {
                    this.final = response.price / 100;
                    this.price = response.old_price / 100;
                    return;
                }

                this.final = response.price;
                this.price = response.old_price;
            } catch (error) {
                this.error = error;
                if (
                    this.error.non_field_errors &&
                    this.error.non_field_errors.indexOf(
                        `This speaker can't be use with this limit`
                    ) > -1
                ) {
                    this.speaker = null;
                }
            }
        },

        async getLink() {
            if (!this.ready) return;
            this.loading = true;
            this.error = null;
            try {
                const data = {
                    limit: this.limit,
                    content_type: this.bookContentTypeId,
                    object_id: this.bookId,
                    type_payment: 'buy',
                    promo_code: this.promo,
                    speaker: this.speaker,
                };

                this.loadingText = this.$t('payment.redirect');

                const redirectUrl = await fetchLink(data);

                if (redirectUrl) {
                    if (redirectUrl.includes('session_id=')) {
                        let { session_id: sessionId } = parseQuery(redirectUrl);

                        if (!sessionId || !window.stripeInstance) {
                            console.error('Error: no stripe instance or session_id');
                            return;
                        }

                        window.stripeInstance.redirectToCheckout({
                            sessionId,
                        });
                    } else {
                        window.location = redirectUrl;
                    }

                    return;
                }

                this.loadingText = this.$t('misc.loading');
                setTimeout(() => {
                    this.$router.replace({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            payment: 'success',
                        },
                    });
                }, 2000);
            } catch (error) {
                this.loading = false;
                this.error = error;
                console.log(error);
            }
        },

        async checkPayment(id) {
            try {
                this.loading = true;
                const log = await checkPayment(id);

                if (log.status === 'succeeded') {
                    this.successType = 'has-limit';
                } else {
                    this.successType = 'no-limit';
                }

                this.loading = false;
            } catch (error) {
                this.loading = false;
                console.log(error);
            }
        },

        ok() {
            const query = { ...this.$route.query };

            if (query.payment) {
                delete query.payment;
                delete query.receipt_id;
                this.$emit('buyed');
            }

            this.$router.push({ ...this.$route, query });
            this.$emit('close');
        },

        formatCurrency(price) {
            return this.userCurrency === 'RUB' ? `${price.toFixed(2)} ₽` : `$ ${price.toFixed(2)}`;
        },
    },
};
</script>

<style lang="scss" src="./Payment.scss"></style>
